import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  reportsService,
  type CfdiReportFilters,
  type CfdiReportResult,
  type CfdiReportRow,
  type CfdiReportStatus,
  type CfdiReportType,
} from '../../../services/reports.service'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'

const DEFAULT_FILTERS: Record<CfdiReportType, CfdiReportFilters> = {
  invoices: { status: 'all' },
  'credit-notes': { status: 'all' },
}
const STATUS_OPTIONS: Array<{ value: CfdiReportStatus; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Vigente' },
  { value: 'canceled', label: 'Cancelado' },
  { value: 'pending', label: 'Pendiente' },
]
const HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const CELL_CLASS = 'px-6 py-4 text-table-foreground'

export interface AppliedCfdiReportContext {
  type: CfdiReportType
  filters: CfdiReportFilters
}

interface CfdiTabProps {
  activeReport: CfdiReportType
  onActiveReportChange: (report: CfdiReportType) => void
  onAppliedContextChange: (context: AppliedCfdiReportContext) => void
}

export function CfdiTab({
  activeReport,
  onActiveReportChange,
  onAppliedContextChange,
}: CfdiTabProps) {
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const currentDraft = draftFilters[activeReport]
  const currentApplied = appliedFilters[activeReport]
  const queryFilters = useMemo(
    () => ({ ...currentApplied, page }),
    [currentApplied, page],
  )
  const reportQuery = useQuery({
    queryKey: ['reports', 'cfdi', activeReport, queryFilters],
    queryFn: () => reportsService.getCfdiReport(activeReport, queryFilters),
    placeholderData: (previous) =>
      previous?.report === activeReport ? previous : undefined,
  })

  useEffect(() => {
    onAppliedContextChange({ type: activeReport, filters: currentApplied })
  }, [activeReport, currentApplied, onAppliedContextChange])

  const updateDraft = (changes: Partial<CfdiReportFilters>) => {
    setDraftFilters((current) => ({
      ...current,
      [activeReport]: { ...current[activeReport], ...changes },
    }))
  }

  const applyFilters = () => {
    setAppliedFilters((current) => ({
      ...current,
      [activeReport]: { ...currentDraft },
    }))
    setPage(1)
  }

  const clearFilters = () => {
    setDraftFilters((current) => ({
      ...current,
      [activeReport]: { ...DEFAULT_FILTERS[activeReport] },
    }))
    setAppliedFilters((current) => ({
      ...current,
      [activeReport]: { ...DEFAULT_FILTERS[activeReport] },
    }))
    setPage(1)
  }

  const changeReport = (value: string) => {
    onActiveReportChange(value as CfdiReportType)
    setPage(1)
  }

  return (
    <TabsContent value="cfdi" className="space-y-5">
      <Tabs
        value={activeReport}
        onValueChange={changeReport}
        className="space-y-5"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="invoices" className="text-xs sm:text-sm">
            Facturas (Impuestos)
          </TabsTrigger>
          <TabsTrigger value="credit-notes" className="text-xs sm:text-sm">
            Notas de Crédito
          </TabsTrigger>
        </TabsList>

        <GlobalFilterBar
          show={['dates']}
          values={{
            startDate: String(currentDraft.date_from ?? ''),
            endDate: String(currentDraft.date_to ?? ''),
          }}
          onChange={(changes) =>
            updateDraft({
              ...(changes.startDate !== undefined
                ? { date_from: changes.startDate || undefined }
                : {}),
              ...(changes.endDate !== undefined
                ? { date_to: changes.endDate || undefined }
                : {}),
            })
          }
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters(activeReport, currentDraft)}
        >
          <Field label="Estado">
            <Select
              value={String(currentDraft.status ?? 'all')}
              onValueChange={(value) =>
                updateDraft({ status: value as CfdiReportStatus })
              }
            >
              <SelectTrigger aria-label="Estado">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-end">
            <Button type="button" onClick={applyFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Aplicar filtros
            </Button>
          </div>
        </GlobalFilterBar>

        <CfdiResults
          type={activeReport}
          result={reportQuery.data}
          isLoading={reportQuery.isLoading}
          isFetching={reportQuery.isFetching}
          error={reportQuery.error}
          onPageChange={setPage}
        />
      </Tabs>
    </TabsContent>
  )
}

function CfdiResults({
  type,
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  type: CfdiReportType
  result?: CfdiReportResult
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  const headers = [
    'Fecha de emisión',
    'Folio fiscal',
    'Serie/Folio',
    'Cliente',
    'RFC',
    'Estado',
    'Subtotal',
    'Método de pago',
    'Total',
  ]

  if (isLoading) {
    return (
      <TableSkeleton
        headers={headers}
        columns={headers.map(() => 'text')}
        rows={10}
      />
    )
  }
  if (error) {
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  }
  if (!result || result.rows.length === 0) {
    return (
      <ReportState
        message={
          type === 'invoices'
            ? 'No hay facturas para los filtros aplicados.'
            : 'No hay notas de crédito para los filtros aplicados.'
        }
      />
    )
  }

  return (
    <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
      <div className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <h3 className="text-table-foreground font-semibold">Resultados</h3>
          <p className="text-muted-foreground text-xs">
            {result.pagination.total} comprobantes filtrados
          </p>
        </div>
        {isFetching && (
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-max">
          <TableHeader className="border-border bg-table-header-bg border-b">
            <TableRow className="hover:bg-transparent">
              {headers.map((header, index) => (
                <TableHead
                  key={header}
                  className={`${HEAD_CLASS} ${index === 6 || index === 8 ? 'text-right' : ''}`}
                >
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="[&_tr]:border-border">
            {result.rows.map((row, index) => (
              <TableRow
                key={`${row.fiscal_uuid}-${row.date}-${index}`}
                className="hover:bg-table-row-hover"
              >
                <TableCell className={CELL_CLASS}>
                  {formatDate(row.date)}
                </TableCell>
                <TableCell className={`${CELL_CLASS} font-mono text-xs`}>
                  {row.fiscal_uuid}
                </TableCell>
                <TableCell className={`${CELL_CLASS} font-medium`}>
                  {row.folio}
                </TableCell>
                <TableCell className={CELL_CLASS}>{row.customer}</TableCell>
                <TableCell className={`${CELL_CLASS} font-mono text-xs`}>
                  {row.receiver_rfc}
                </TableCell>
                <TableCell className={CELL_CLASS}>
                  <StatusBadge status={row.status} />
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-right tabular-nums`}>
                  {formatMoney(row.subtotal)}
                </TableCell>
                <TableCell className={CELL_CLASS}>
                  {row.payment_method}
                </TableCell>
                <TableCell
                  className={`${CELL_CLASS} text-right font-semibold tabular-nums`}
                >
                  {formatMoney(row.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter className="border-border bg-table-header-bg border-t font-semibold">
            <TableRow className="hover:bg-transparent">
              <TableCell className={CELL_CLASS}>TOTAL GENERAL</TableCell>
              <TableCell className={CELL_CLASS}>
                Registros: {result.totals.record_count}
              </TableCell>
              <TableCell className={CELL_CLASS} />
              <TableCell className={CELL_CLASS} />
              <TableCell className={CELL_CLASS} />
              <TableCell className={CELL_CLASS} />
              <TableCell className={`${CELL_CLASS} text-right tabular-nums`}>
                {formatMoney(result.totals.subtotal)}
              </TableCell>
              <TableCell className={CELL_CLASS} />
              <TableCell className={`${CELL_CLASS} text-right tabular-nums`}>
                {formatMoney(result.totals.total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function StatusBadge({ status }: { status: CfdiReportRow['status'] }) {
  const classes =
    status === 'Vigente'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'Cancelado'
        ? 'bg-red-100 text-red-700'
        : 'bg-amber-100 text-amber-700'

  return (
    <span className={`rounded-full px-2 py-1 text-xs font-medium ${classes}`}>
      {status}
    </span>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function hasActiveFilters(type: CfdiReportType, filters: CfdiReportFilters) {
  const defaults = DEFAULT_FILTERS[type]
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filters)])
  return Array.from(keys).some((key) => filters[key] !== defaults[key])
}

function formatMoney(value: number) {
  return value.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
  })
}

function formatDate(value: string) {
  const date = new Date(value.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}
